import React from "react";
import { FaInstagram } from "react-icons/fa"
export default function WriteWithAI() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      {/* subtle glow accents */}
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            How to write with AI and still get a good grade
          </h1>
          <p className="mt-3 text-gray-300">
            Learn How to write with AI and still get a good grade, ethical workflow, APA/MLA citations, AI disclosure, revision tips, and originality checks.
          </p>
         
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* TOC (sticky on desktop) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#intro" className="block hover:text-white">Introduction</a>
                <a href="#keypoints" className="block hover:text-white">Key points</a>
                <a href="#workflow" className="block hover:text-white">The ethical workflow</a>
                <a href="#citing" className="block hover:text-white">Citing & disclosing AI</a>
                <a href="#detectors" className="block hover:text-white">AI detectors</a>
                <a href="#uk" className="block hover:text-white">UK context</a>
                <a href="#discipline" className="block hover:text-white">Discipline mini-playbooks</a>
                <a href="#accused" className="block hover:text-white">If accused of AI misuse</a>
                <a href="#quick" className="block hover:text-white">Quick workflow</a>
                <a href="#support" className="block hover:text-white">Support</a>
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#conclusion" className="block hover:text-white">Conclusion</a>
                 <a href="#acknowledgement" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-10">
            {/* Introduction */}
            <section id="intro" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Introduction</h2>
              <p className="text-gray-200">
                How to write with AI and still get a good grade comes down to five moves: plan to your grading rubric, build a tight outline, draft with an AI writing assistant at paragraph level (not end-to-end), add correct in-text citations and a brief AI disclosure when required, then finish with human revision for critical analysis, clarity, and coherence.
              </p>
              <p className="text-gray-200 mt-3">
                In practice, that means starting with a clear thesis, organizing evidence from primary sources in Zotero/Mendeley, drafting with low randomness, formatting in APA 7th edition or Harvard referencing, checking originality in Turnitin, and polishing with Grammarly/Hemingway Editor, so the final work is authentically yours and assessment-ready.
              </p>
            </section>

            {/* Key points */}
            <section id="keypoints" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Key points</h2>
              <ul className="list-disc pl-6 space-y-3 text-gray-200">
                <li>Respect academic integrity & the rubric. Confirm what AI help is permitted, align to learning outcomes, and set rules for AI disclosure and citation style.</li>

                <li>Plan with a thesis and outline. Write a clear thesis, craft topic sentences, map evidence, and note transition words to guide flow and coherence.</li>

                <li>Draft with AI, one paragraph at a time. Feed bullets to your tool, keep randomness low, forbid fabricated sources, and add citation placeholders as you write.</li>

                <li>Revise like a human for insight and clarity. Add critical analysis, counterpoints, and smoother linking; polish with active voice using tools like Grammarly/Hemingway Editor.</li>

                <li>Finish with citations, disclosure, and originality checks. Format in-text citations and the reference list (APA 7/Harvard/MLA), add a short disclosure if required, and verify with Turnitin.</li>
              </ul>
            </section>

            {/* Workflow */}
            <section id="workflow" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">How to write with AI and still get a good grade: the ethical workflow</h2>
              <ol className="list-decimal pl-6 space-y-2 text-gray-200">
                <li>
                  <strong>Integrity first, rules up front.</strong> Skim your course syllabus and academic integrity policy. Note whether AI disclosure is required, whether generated text is permitted, and which referencing style (APA 7th edition, Harvard referencing, MLA, or Chicago Manual of Style) you must follow.
                </li>
                <li>
                  <strong>Start with your own thinking.</strong> Draft a precise thesis statement and list 3–4 claims you can defend with evidence. Map these to your learning outcomes and the grading rubric verbs (analyze, evaluate, synthesize).
                </li>
                <li>
                  <strong>Outline before you generate.</strong> Create a paragraph-level outline: every paragraph gets a topic sentence, planned evidence, and a sentence that ties back to your thesis. Add transition words you’ll use to guide the reader (e.g., “However,” “By contrast,” “Therefore”).
                </li>
                <li>
                  <strong>Use AI to accelerate, not replace.</strong> Ask ChatGPT, Gemini, or Copilot to draft one paragraph at a time from your topic sentence and bullet points. Keep temperature low for factual sections. Paste citation placeholders while drafting.
                </li>
                <li>
                  <strong>Human revision is where grades are made.</strong> Do a “substance pass” (argument quality, critical analysis, evidence strength) and a “style pass” (clarity, coherence, active voice). Run Grammarly for grammar and Hemingway Editor for readability; accept only edits that preserve meaning.
                </li>
                <li>
                  <strong>Final compliance.</strong> Insert correct in-text citations, build the reference list, check formatting against APA 7, Harvard, MLA, or Chicago, and generate an originality report with Turnitin or your institution’s plagiarism checker.
                </li>
              </ol>
            </section>

            {/* Citing & disclosing AI */}
            <section id="citing" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Citing and disclosing AI (APA/MLA/Chicago): practical templates</h2>
              <p className="text-gray-200">
                Policies vary, so follow your instructor’s rules first. When permitted or required to attribute AI use, these plain-English templates help you do it cleanly.
              </p>

              <p className="text-gray-200 font-semibold mt-3">How to disclose AI use (two-line models you can adapt):</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>
                  Disclosure (methods note): “I used ChatGPT to brainstorm headings and to improve sentence clarity. All arguments, structure, and sources were selected and verified by me.”
                </li>
                <li>
                  Disclosure (acknowledgment): “I used an AI writing assistant for language polishing only; ideas, analysis, and citations are my own.”
                </li>
              </ul>

              <p className="text-gray-200 mt-3">
                <strong>APA 7th edition (common approach):</strong>
                Reference entry (software): OpenAI. (2025). ChatGPT (Aug 2025 release) [Large language model].
              </p>
              <p className="text-gray-200">In-text: (OpenAI, 2025).</p>
              <p className="text-gray-200">
                Describe prompts in the text if relevant: “Using ChatGPT to paraphrase my draft for clarity (prompt dated 15 Aug 2025)…”
              </p>

              <p className="text-gray-200 mt-3"><strong>MLA (common approach):</strong> Works Cited: ChatGPT. ChatGPT. OpenAI, 2025.</p>
              <p className="text-gray-200">In-text: (ChatGPT).</p>
              <p className="text-gray-200">Optional: Add the date of interaction or prompt description in the sentence.</p>

              <p className="text-gray-200 mt-3"><strong>Chicago (Notes & Bibliography):</strong> Footnote (example): “Text generated with ChatGPT (OpenAI), prompt ‘Explain X for a second-year sociology essay,’ accessed 15 Aug 2025.”</p>
              <p className="text-gray-200">Bibliography (software entry optional): OpenAI. ChatGPT. 2025.</p>
              <p className="text-gray-200">Tip: Include model/version and the date you interacted with the tool. If your department prefers treating AI responses as “personal communication,” follow that local convention.</p>
            </section>

            {/* Detectors */}
            <section id="detectors" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Can AI detectors really tell? What markers actually look for</h2>
              <p className="text-gray-200">
                AI detectors are imperfect; they can mislabel human text and are sensitive to length and edits. Instead of writing to “evade” a tool, show authentic authorship:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Keep a draft trail (outline, dated notes, paragraph iterations).</li>
                <li>Maintain a Zotero/Mendeley library with highlights and page numbers.</li>
                <li>Save your paraphrasing notes beside the source quotes and add your commentary (“So what?”).</li>
                <li>Demonstrate critical analysis: compare studies, note limitations, and justify your choices.</li>
                <li>Be transparent with a short AI disclosure when your policy asks for it.</li>
              </ul>
            </section>

            {/* UK context */}
            <section id="uk" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">UK context at a glance (helps your credibility if you study in the UK)</h2>
              <p className="text-gray-200">
                For UK readers, refer to sector expectations from JCQ, Ofqual, and QAA: institutions typically value transparency, proper attribution, and clear boundaries between assistance and authorship.
                Many universities encourage student AI literacy while requiring that arguments and interpretations remain the student’s own work. When in doubt, ask your module leader how to phrase your AI disclosure and which citation style to use for AI tools.
              </p>
            </section>

            {/* Discipline mini-playbooks */}
            <section id="discipline" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Discipline-specific mini-playbooks (apply and adapt)</h2>

              <div className="mt-3">
                <h3 className="font-semibold text-white">Psychology (literature review):</h3>
                <p className="text-gray-200">
                  Outline by mechanism/theme (e.g., social comparison → anxiety → interventions). Use primary sources for findings; secondary sources for context. Ask AI to draft a comparison paragraph (two studies, 120–150 words), then you add critique on sample size, measurement tools, and generalizability. Close each paragraph by tying the evidence to your thesis.
                </p>
              </div>

              <div className="mt-3">
                <h3 className="font-semibold text-white">Business (case memo/report):</h3>
                <p className="text-gray-200">
                  Structure: market context → strategy → metrics → risk. Feed topic sentences plus 3 bullet points (e.g., revenue growth, margin, CAC/LTV) to AI for a clean first pass. You add sensitivity analysis, scenario planning, and a short counterargument, then cite data properly in Harvard referencing.
                </p>
              </div>

              <div className="mt-3">
                <h3 className="font-semibold text-white">Engineering (lab report):</h3>
                <p className="text-gray-200">
                  Sections: aim → method → results → analysis → limitations → recommendations. Use AI to rephrase methods for clarity while preserving technical accuracy. You add uncertainty quantification, instrument tolerance, and failure modes. Conform to required style (APA 7 or IEEE if specified).
                </p>
              </div>

              <div className="mt-3">
                <h3 className="font-semibold text-white">Computer Science (programming assignment):</h3>
                <p className="text-gray-200">
                  Write pseudocode and comments first. If policy allows, use AI for code explanations or refactoring ideas, not for final logic. Keep a Git commit trail with messages that explain your reasoning. In the report, analyze complexity, constraints, and trade-offs in your own words.
                </p>
              </div>
            </section>

            {/* Accused */}
            <section id="accused" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">If you’re accused of AI misuse: a calm three-step protocol</h2>
              <ol className="list-decimal pl-6 space-y-2 text-gray-200">
                <li>Show authorship evidence. Provide your outline, draft versions, notes with timestamps, and your reference manager exports.</li>
                <li>Walk through your reasoning. Explain how you selected sources, why you paraphrased certain sections, where you added critical analysis, and how you aligned with the grading rubric.</li>
                <li>Ask for constructive resolution. Offer to discuss your process in a short viva/meeting and to revise with clearer AI disclosure if needed.</li>
              </ol>
            </section>

            {/* Quick workflow */}
            <section id="quick" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Quick workflow</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Read the brief; mark learning outcomes and rubric verbs.</li>
                <li>Write a one-sentence thesis statement that answers the question.</li>
                <li>Build a paragraph-level outline with topic sentences and planned evidence.</li>
                <li>Draft each paragraph with AI from your bullets; keep the voice consistent.</li>
                <li>Insert in-text citations as you go; track sources in Zotero/Mendeley.</li>
                <li>Do a substance pass (argument, evidence, counterpoints), then a style pass (clarity, coherence, active voice).</li>
                <li>Add AI disclosure if required; format reference list in APA 7, Harvard, MLA, or Chicago.</li>
                <li>Run a plagiarism checker; fix any accidental close paraphrases; submit confidently.</li>
              </ul>

              <p className="text-gray-200 mt-4">
                If you’d like hands-on, ethical support while you implement this workflow, Infinite Solution can help you plan, outline, and refine your draft without compromising academic integrity.
                Our subject-matter experts guide you through thesis framing, paragraph-level outlining (with strong topic sentences and transitions), evidence selection, and critical analysis, then polish clarity with human proofreading and style edits (think active voice, cohesion, readability).
                We also handle compliance APA 7th edition, Harvard referencing, MLA adding correct in-text citations and a clean reference list. For peace of mind, we run an originality check with a trusted plagiarism checker (e.g., Turnitin) and can draft a brief AI disclosure when required.
                Tools like Zotero, Mendeley, Grammarly, and Hemingway Editor are integrated into our process, and our team offers responsive guidance for UK, Europe, Australia, and the USA. You stay the author; we make the writing sharper, faster, and fully compliant.
              </p>
            </section>

            {/* Support / FAQs */}
            <section id="support" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">FAQs</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>
                  <strong>How to write with AI and still get a good grade without breaking rules?</strong>
                  Use AI for brainstorming, outlining, and language polishing; keep the ideas, analysis, and structure your own. Attribute AI assistance if your policy requires it, and always provide proper citations.
                </li>
                <li>
                  <strong>Can Turnitin detect AI with certainty?</strong>
                  No detector is definitive. Focus on authentic authorship and keep process evidence (drafts, notes, citations). If asked, you can explain your workflow and show your AI disclosure.
                </li>
                <li>
                  <strong>Is paraphrasing AI output okay?</strong>
                  Only if your policy allows AI assistance and you verify facts, integrate primary sources, and add your own critical analysis. Paraphrasing alone isn’t originality.
                </li>
                <li>
                  <strong>What should an AI disclosure look like?</strong>
                  Two short models were provided above; pick one and adapt to your assignment type and departmental norms.
                </li>
                <li>
                  <strong>How do I protect privacy and IP?</strong>
                  Don’t paste confidential data, unpublished research, or personal information into public tools. Work locally for sensitive material.
                </li>
              </ul>
            </section>

            {/* Conclusion */}
            <section id="conclusion" style={{ scrollMarginTop: "7rem" }} className="bg-white/5 rounded-xl border border-white/10 p-6">
              <h2 className="text-2xl font-bold mb-3">Conclusion</h2>
              <p className="text-gray-200">
                You can absolutely write with AI and still get a good grade, if you anchor your process to academic integrity and your grading rubric. Plan your argument, outline at paragraph level, and draft with controlled prompts so your voice leads.
                Attribute AI help where required, cite responsibly in APA 7, Harvard, MLA, or Chicago, and finish with deliberate human revision that showcases critical analysis and coherence. The result is work that reads naturally, demonstrates real understanding, and earns the grade you’re aiming for.
              </p>
            </section>

            <section
              id="acknowledgement"
              style={{ scrollMarginTop: "7rem" }}
              className="bg-gradient-to-r from-indigo-900/40 via-purple-900/30 to-pink-900/20 rounded-xl border border-white/10 p-6 mt-10 shadow-lg"
            >
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
                Acknowledgement
              </h2>
            
              <p className="text-gray-200">
                The concept was first introduced by{" "}
                <span className="font-semibold text-white">Abhiishek Shaw</span>, who set the foundation for the content’s direction.
                <br />
                <a
                  href="https://www.instagram.com/abhishekshawofficial_?igsh=Ynh2OXl6bm4xN3Z4"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-pink-400 hover:text-pink-300 font-semibold"
                >
                  <FaInstagram /> @abhishekshaw
                </a>
              </p>
            
              <p className="text-gray-200 mt-4">
                To shape the initial draft, we utilized advanced AI tools like <span className="font-semibold">ChatGPT</span> and{" "}
                <span className="font-semibold">Google Gemini</span>, ensuring speed, structure, and creativity. Once the draft was
                developed, our team at{" "}
                <a
                  href="https://www.infinitesolutionedu.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 font-semibold hover:text-blue-300 underline"
                >
                  Infinite Solution
                </a>{" "}
                took over to polish it, optimizing for SEO, refining the tone for clarity, and aligning the message with the target
                audience.
              </p>
            
              <p className="text-gray-200 mt-4">
                This collaborative approach seamlessly blended human expertise with AI-driven efficiency, resulting in content that
                is impactful, high-performing, and highly relevant to readers.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
}
